import React, { forwardRef } from "react"; // Import forwardRef (appies ref to all children)
import PropTypes from "prop-types";
import styles from "./FeedItemModal.module.css";
import CloseButton from "../CloseButton";
import StandingOrderIcon from "../StandingOrderIcon";
import classNames from "classnames";
import { DisplaySectionContent } from "../DisplaySectionContent/DisplaySectionContent";

/*
 * Viewcomponent. A modal pop-up for a feed item.
 */

// Refactor to include ref for DOMNode integrity
export const FeedItemModal = forwardRef(
  ({ isOpen, setIsOpen, feedItem }, ref) => {
    // Fail-safe section resolver
    const sections =
      feedItem?.content?.sections ||
      feedItem?.data?.card?.content?.sections ||
      feedItem?.information?.content?.sections ||
      [];

    if (feedItem.type === "Banner") return null; // Bypass modal for banners
    return (
      <>
        {isOpen && (
          <div className={styles.overlay} ref={ref}>
            {" "}
            {/* Add ref */}
            <div className={styles.container}>
              <div
                className={styles.cardContainer}
                style={{
                  background: `linear-gradient(${
                    feedItem.type === "Standing Order"
                      ? (feedItem.data?.card?.gradient?.angle ?? 0) + "deg"
                      : (feedItem.information?.gradientAngle ?? 0) + "deg"
                  }, ${
                    feedItem.type === "Standing Order"
                      ? Array.isArray(feedItem.data?.card?.gradient?.stops)
                        ? feedItem.data.card.gradient.stops.map(
                            (stop) => " " + (stop.color || stop)
                          )
                        : ""
                      : Array.isArray(feedItem.information?.gradientColors)
                      ? feedItem.information.gradientColors
                          .map((color) => {
                            if (!color || typeof color !== "string") {
                              return "";
                            }

                            const rgbMatch = color.match(
                              /rgb\((\d+),\s*(\d+),\s*(\d+)\)/
                            );
                            if (rgbMatch) {
                              return `rgb(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]})`;
                            }

                            const hexMatch = color.match(
                              /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/
                            );
                            if (hexMatch) {
                              return color;
                            }

                            const parts = color.split(" ");
                            return parts.length > 1 ? parts[1] : color;
                          })
                          .filter(Boolean)
                          .join(", ")
                      : ""
                  })`,
                }}
              >
                <section className={styles.content}>
                  {feedItem.type === "Standard Content" ? (
                    <>
                      <div className={styles.grid}>
                        {/* Update with safeguards for backgroundColor when undefined */}
                        <div
                          className={styles["header-background"]}
                          style={{
                            backgroundColor:
                              feedItem.information?.headerBackground
                                ?.backgroundColor || "#ffffff",
                            opacity:
                              feedItem.information?.headerBackground
                                ?.backgroundOpacity || "1.0",
                          }}
                        ></div>

                        <div className={styles["header-container"]}>
                          <div className={styles["button-container"]}>
                            <h1
                              className={styles["standard-header"]}
                              // Safeguard for clean rendering when undefined
                              style={{
                                color:
                                  feedItem.information?.headerTextColor ||
                                  "#000",
                              }}
                            >
                              {feedItem.information.header}
                            </h1>
                            <div className={styles["close-button"]}>
                              <CloseButton
                                color={"black"}
                                backgroundColor={"#fff"}
                                border={true}
                                onClick={setIsOpen}
                              />
                            </div>
                          </div>

                          {feedItem.information.subHeader && (
                            <h2
                              className={styles["sub-header"]}
                              // Safeguard for clean rendering when undefined
                              style={{
                                color:
                                  feedItem.information?.subHeaderTextColor ||
                                  "#000",
                              }}
                            >
                              {feedItem.information.subHeader}
                            </h2>
                          )}
                        </div>
                      </div>
                      <div className={styles["img-container"]}>
                        <div
                          className={classNames(styles["img-div"], {
                            // Safeguard for clean rendering when undefined
                            [styles["no-padding"]]:
                              feedItem.information?.backgroundType ===
                              "Full Background",
                            [styles.left]:
                              feedItem.information?.horizontalAlign === "left",
                            [styles.right]:
                              feedItem.information?.horizontalAlign === "right",
                            [styles.center]:
                              feedItem.information?.horizontalAlign ===
                              "center",
                            [styles.bottom]:
                              feedItem.information?.bottomPadding,
                          })}
                          // Safeguard for clean rendering when undefined
                          style={
                            feedItem.information?.backgroundType ===
                            "Full Background"
                              ? {
                                  backgroundImage: `url(${feedItem.imgSrc})`, // Update to use image normalization
                                  backgroundPosition: `center`,
                                  backgroundRepeat: "no-repeat",
                                  backgroundSize: "cover",
                                }
                              : {}
                          }
                        >
                          {feedItem.information?.backgroundType ===
                            "On Gradient" &&
                            feedItem.imgSrc && (
                              <img
                                src={feedItem.imgSrc}
                                alt="News Card thumbnail"
                                style={{
                                  width: feedItem.information?.maxHeight,
                                }}
                              />
                            )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={styles["so-close"]}>
                        <CloseButton
                          color={"black"}
                          backgroundColor={"#fff"}
                          border={true}
                          onClick={setIsOpen}
                        />
                      </div>

                      <p
                        className={styles["so-pretitle"]}
                        style={{ color: feedItem.data?.card?.header?.topTitle?.color }}
                      >
                        {feedItem.data?.card?.header?.topTitle?.text}
                      </p>

                      <div className={styles["standing-order-flex"]}>
                        <div className={styles["standing-order-header"]}>
                          <h1
                            style={{ color: feedItem.data?.card?.header?.title?.color }}
                          >
                            {feedItem.data?.card?.header?.title?.text || "Untitled"}
                          </h1>
                          <h2
                            style={{ color: feedItem.data?.card?.header?.subTitle?.color }}
                          >
                            {feedItem.data?.card?.header?.subTitle?.text}
                          </h2>
                        </div>
                        <div>
                          <StandingOrderIcon
                            iconType={feedItem.data?.card?.image?.path ?? undefined}
                            iconSize="120px"
                          />
                          <p
                            className={styles["so-icon-text"]}
                            style={{ color: feedItem.data?.card?.image?.subText?.color }}
                          >
                            {feedItem.data?.card?.image?.subText?.text}
                          </p>
                        </div>
                      </div>
                      <div className={styles["standing-order-details"]}>
                        <p style={{ color: feedItem.data?.card?.details?.sku?.color }}>
                          {feedItem.data?.card?.details?.sku?.text}
                        </p>
                        <p style={{ color: feedItem.data?.card?.details?.creator?.color }}>
                          {feedItem.data?.card?.details?.creator?.text}
                        </p>
                        <p style={{ color: feedItem.data?.card?.details?.title?.color }}>
                          {feedItem.data?.card?.details?.title?.text}
                        </p>
                      </div>
                    </>
                  )}
                </section>
              </div>

              <section className={styles["display-container"]}>
                {/* Refactor with safeguards for sections */}
                <DisplaySectionContent
                  sectionElements={sections}
                  isHidden={false}
                />
              </section>
            </div>
          </div>
        )}
      </>
    );
  }
);

FeedItemModal.propTypes = {
  // Boolean that determines whether the modal is visible or not.
  isOpen: PropTypes.bool.isRequired,

  // Function that sets the value for isOpen.
  setIsOpen: PropTypes.func.isRequired,

  // Feed item object: can be Standard Content, Standing Order, or Banner.
  feedItem: PropTypes.shape({
    type: PropTypes.oneOf(["Standard Content", "Standing Order", "Banner"])
      .isRequired,

    // Used in Standard Content
    information: PropTypes.object,
    content: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.arrayOf(PropTypes.object),
    ]),

    // Used in Standing Order
    options: PropTypes.object,
    data: PropTypes.shape({
      card: PropTypes.shape({
        details: PropTypes.object,
        gradient: PropTypes.shape({
          angle: PropTypes.number,
          stops: PropTypes.arrayOf(
            PropTypes.shape({
              color: PropTypes.string,
            })
          ),
        }),
        header: PropTypes.shape({
          topTitle: PropTypes.shape({
            text: PropTypes.string,
            color: PropTypes.string,
          }),
          title: PropTypes.shape({
            text: PropTypes.string,
            color: PropTypes.string,
          }),
          subTitle: PropTypes.shape({
            text: PropTypes.string,
            color: PropTypes.string,
          }),
        }),
        image: PropTypes.shape({
          path: PropTypes.string,
        }),
        content: PropTypes.shape({
          sections: PropTypes.oneOfType([
            PropTypes.arrayOf(PropTypes.object),
            PropTypes.object,
          ]),
        }),
      }),
    }),
  }).isRequired,
};

export default FeedItemModal;
